import { useEffect } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { trackEvent } from './lib/tracking';
import { WHATSAPP_BASE, WHATSAPP_GENERIC_MESSAGE } from './lib/constants';
import LpHeader from './components/LpHeader';
import MobileCtaBar from './components/MobileCtaBar';
import HeroSection from './components/HeroSection';
import ProvaCapacidade from './components/ProvaCapacidade';
import ParaQuemSection from './components/ParaQuemSection';
import SolucoesSection from './components/SolucoesSection';
import CasosSection from './components/CasosSection';
import ComoFuncionaSection from './components/ComoFuncionaSection';
import QualificacaoForm from './components/QualificacaoForm';
import InvestimentoSection from './components/InvestimentoSection';
import FaqSection from './components/FaqSection';
import LpFooter from './components/LpFooter';

const faqSchemaItems = [
  {
    q: 'A Home Office Premium faz projeto completo de escritório?',
    a: 'Sim. Atuamos no projeto e implantação de ambientes corporativos completos: mobiliário, divisórias, carpetes, acústica e marcenaria, do planejamento à entrega e montagem.',
  },
  {
    q: 'Vocês atendem empresas que estão montando uma nova sede?',
    a: 'Sim. Novas sedes são um dos nossos principais focos. Estruturamos toda a ambientação da nova operação, com solução única para todos os ambientes.',
  },
  {
    q: 'Vocês trabalham com arquitetos?',
    a: 'Sim. Executamos e fornecemos soluções de acordo com a especificação do profissional, integrando mobiliário, divisórias e demais componentes ao projeto.',
  },
  {
    q: 'Vocês fornecem mobiliário e divisórias no mesmo projeto?',
    a: 'Sim. Centralizamos mobiliário, divisórias, carpetes, acústica e marcenaria em um único parceiro, simplificando a coordenação do projeto.',
  },
  {
    q: 'Vocês trabalham com carpete corporativo?',
    a: 'Sim. Fornecemos carpete modular e soluções de piso para áreas empresariais, integradas ao restante do projeto.',
  },
  {
    q: 'Vocês atendem fora de Caruaru?',
    a: 'Sim. Atendemos projetos em Pernambuco e avaliamos projetos em todo o Nordeste conforme porte e logística.',
  },
  {
    q: 'Qual o investimento mínimo?',
    a: 'Nosso foco são projetos corporativos a partir de R$ 10 mil. O valor final depende dos ambientes, usuários, produtos e serviços envolvidos.',
  },
  {
    q: 'Quanto tempo leva para receber uma proposta?',
    a: 'Após recebermos as informações do seu projeto (planta, medidas, fotos ou projeto do arquiteto), estruturamos a proposta comercial e o escopo.',
  },
  {
    q: 'A entrega e montagem estão incluídas?',
    a: 'Sim. Coordenamos o fornecimento, a logística e a implantação, garantindo a entrega e montagem de todos os componentes do projeto.',
  },
];

export default function ProjetosCorporativosPage() {
  useSEO({
    title: 'Projetos Corporativos | Mobiliário, Divisórias e Implantação',
    description:
      'Projetos corporativos completos com mobiliário, divisórias, carpetes, acústica, entrega e montagem. Atendimento a empresas em Pernambuco e Nordeste.',
    keywords:
      'projeto corporativo, mobiliário corporativo, móveis corporativos, divisórias corporativas, carpete corporativo, implantação de escritório',
    path: '/projetos-corporativos',
  });

  // Tracking de scroll (50% e 90%)
  useEffect(() => {
    let fired50 = false;
    let fired90 = false;

    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      const pct = (window.scrollY / total) * 100;
      if (!fired50 && pct >= 50) {
        fired50 = true;
        trackEvent('scroll_50');
      }
      if (!fired90 && pct >= 90) {
        fired90 = true;
        trackEvent('scroll_90');
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Dados estruturados (LocalBusiness, Service, FAQPage, BreadcrumbList)
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com';
    const pageUrl = `${baseUrl}/projetos-corporativos`;

    const schemas: Array<{ id: string; data: Record<string, unknown> }> = [
      {
        id: 'schema-pc-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Início', item: baseUrl },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Projetos Corporativos',
              item: pageUrl,
            },
          ],
        },
      },
      {
        id: 'schema-pc-service',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Projetos Corporativos',
          serviceType: 'Implantação de ambientes corporativos',
          description:
            'Projetos corporativos completos com mobiliário, divisórias, carpetes, acústica, entrega e montagem. Atendimento a empresas em Pernambuco e Nordeste.',
          url: pageUrl,
          areaServed: ['Pernambuco', 'Nordeste'],
          provider: {
            '@type': 'LocalBusiness',
            name: 'Home Office Premium',
            telephone: '+55-81-98203-0285',
            url: baseUrl,
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Av. Agamenon Magalhães, 1519',
              addressLocality: 'Caruaru',
              addressRegion: 'PE',
              postalCode: '55014-000',
              addressCountry: 'BR',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: -8.2836,
              longitude: -36.0078,
            },
          },
        },
      },
      {
        id: 'schema-pc-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqSchemaItems.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      },
      {
        id: 'schema-pc-local',
        data: {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'Home Office Premium',
          description:
            'Soluções completas para ambientes corporativos: mobiliário, divisórias, acústica, carpetes e marcenaria.',
          url: baseUrl,
          telephone: '+55-81-98203-0285',
          email: 'ednilsonleocadio@gmail.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: -8.2836,
            longitude: -36.0078,
          },
        },
      },
    ];

    const scripts: HTMLScriptElement[] = [];
    schemas.forEach(({ id, data }) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      scripts.push(script);
    });

    return () => {
      scripts.forEach((s) => s?.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <LpHeader />

      <HeroSection />
      <ProvaCapacidade />
      <ParaQuemSection />
      <SolucoesSection />
      <CasosSection />
      <ComoFuncionaSection />

      {/* Seção de qualificação do lead */}
      <section id="qualificacao" className="bg-white py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2">
              <p className="text-[12px] md:text-[13px] font-semibold tracking-[0.28em] uppercase text-primary-500 mb-4">
                Análise do projeto
              </p>
              <h2 className="text-foreground-950 text-[28px] md:text-[40px] font-semibold tracking-tight leading-tight mb-6">
                Conte para a gente o momento da sua empresa.
              </h2>
              <p className="text-foreground-500 text-[15px] md:text-[16px] leading-relaxed">
                Responda algumas perguntas rápidas e continue o atendimento direto no WhatsApp —
                suas respostas já seguem preenchidas na mensagem para agilizar a análise.
              </p>
            </div>

            <div className="lg:col-span-3">
              <QualificacaoForm />
            </div>
          </div>
        </div>
      </section>

      <InvestimentoSection />
      <FaqSection />
      <LpFooter />

      {/* Barra CTA mobile */}
      <MobileCtaBar
        formAnchor="qualificacao"
        eventPrefix="projetos"
      />

      {/* WhatsApp float (desktop) */}
      <a
        href={`${WHATSAPP_BASE}?text=${WHATSAPP_GENERIC_MESSAGE}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent('whatsapp_projetos', { location: 'float' })}
        className="fixed bottom-5 right-5 z-[9999] hidden md:flex w-12 h-12 md:w-14 md:h-14 items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all duration-300 cursor-pointer shadow-[0_2px_12px_rgba(0,0,0,0.12)]"
        aria-label="Falar com a Home Office Premium no WhatsApp"
      >
        <i className="ri-whatsapp-line text-xl md:text-2xl"></i>
      </a>
    </div>
  );
}